/**
 * Tenant dashboard V2 shell (COL-533).
 *
 * Nine tabs become five that unlock by journey phase, with a separate set for
 * applicants who have no booking yet. This is built alongside V1 rather than
 * carved out of it: `Dashboard.tsx` is 9.5k lines with no test coverage at
 * all, and the V2 design *merges* Vertrag + Zahlungen + Ein-/Auszug into one
 * page, so extracting those panels would produce components the redesign then
 * dismantles. V1 is left untouched and the flag picks between them.
 *
 * Reuses the panels that are already standalone components — the V1 tabs for
 * check-in, support and referrals are thin wrappers around these same ones.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDocusignSigningReturn } from "@/hooks/useDocusignSigningReturn";
import { useStripePaymentReturn } from "@/hooks/useStripePaymentReturn";
import { getTenantRoomLabels } from "@/lib/tenant-room-copy";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { TabsContent } from "@/components/ui/tabs";
import ComingSoon from "@/components/ComingSoon";
import {
  AccountSettingsDialog,
  type AccountSettingsTab,
} from "@/components/settings/AccountSettingsDialog";
import { TenantLanguagePicker } from "@/components/dashboard/LanguagePickerDialog";
import TenantDashboardFooter from "@/components/tenant/v2/TenantDashboardFooter";
import TenantDashboardSearch from "@/components/tenant/v2/TenantDashboardSearch";
import TenantSupportHelp from "@/components/tenant/v2/TenantSupportHelp";
import TenantApplications from "@/components/tenant/v2/TenantApplications";
import TenantOverview from "@/components/tenant/v2/TenantOverview";
import TenantReferrals from "@/components/tenant/v2/TenantReferrals";
import TenantSavedRooms from "@/components/tenant/v2/TenantSavedRooms";
import TenantRoomPage from "@/components/tenant/v2/TenantRoomPage";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useHideSiteFooter } from "@/hooks/useSiteFooter";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProtocolsForBooking } from "@/hooks/useProtocol";
import { useProtocolActionNeeded } from "@/hooks/useProtocolActionNeeded";
import { useNowTick } from "@/hooks/useNowTick";
import { useScrollToSearchTarget } from "@/hooks/useScrollToSearchTarget";
import { useTenantBooking } from "@/hooks/useTenantBooking";
import {
  useTenantPaymentProofs,
  useTenantPayments,
  useTenantProfileName,
} from "@/hooks/useTenantPayments";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor } from "@/lib/i18n/languages";
import {
  isSearchTarget,
  SEARCH_TARGET_PARAM,
  type SearchTarget,
} from "@/lib/tenant-dashboard-search";
import {
  type DashboardV2Tab,
  FORCE_V1_PARAM,
  FORCE_V1_VALUE,
  resolveActiveTab,
  resolveLegacyTabParam,
  visibleTabsForStage,
  isTenancyStarted,
} from "@/lib/tenant-dashboard-tabs";
import { useTenantMoneyFacts } from "@/hooks/useTenantMoneyFacts";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";
import {
  formatTenantGreeting,
  tenantGreetingSlot,
} from "@/lib/tenant-greeting";
import { type TenantJourneyStageId } from "@/lib/tenantJourney";
import { resolveTenantJourneyFromSources } from "@/lib/tenantJourneySignals";

type Props = {
  /**
   * Override the derived journey stage. Tests use it; the app does not.
   *
   * When absent the stage is derived below from real deposit and protocol
   * signals. It was previously left `null` whenever the caller omitted it,
   * which meant the route — the only real caller — pinned every tenant to the
   * always-available tabs and hid Community and Empfehlungen permanently.
   */
  stage?: TenantJourneyStageId | null;
};

/*
  Stable identity: the hook takes `notify` in its dependency list, and an
  object literal rebuilt each render would re-run the effect continuously.
*/
const DOCUSIGN_RETURN_TOASTS = {
  success: (m: string) => toast.success(m),
  info: (m: string) => toast.info(m),
  error: (m: string) => toast.error(m),
};

export default function TenantDashboardV2({ stage: stageOverride }: Props) {
  const { user } = useAuth();
  /*
    This page brings its own footer, so the site-wide marketing one is hidden
    for as long as it is mounted. It must be a declaration from HERE rather
    than a path check in `Footer.tsx`: /dashboard renders V1 or V2 depending on
    the flag, on `?dashboard=v1` and on a latched payment/signing return, and
    only the component that actually mounted knows which.

    Unconditional, and above every early return, so the rules of hooks hold on
    the loading and error branches too.
  */
  useHideSiteFooter();
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const [searchParams, setSearchParams] = useSearchParams();

  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  const { data: booking, isLoading, isError } = useTenantBooking();
  const hasBooking = !!booking;

  // Journey signals. The deposit half comes from the SAME shared derivation
  // the overview uses — previously these were derived separately, and a fix
  // to the waived-deposit bug landed in the overview while this kept the old
  // predicate, so a resident with a waived deposit stayed pinned at
  // deposit_pending with Community and Empfehlungen hidden.
  /*
    Own the DocuSign return hop.

    DocuSign lands the tenant back on /dashboard with `docusign=complete`
    and its own `event`. DashboardRoute used to latch those to V1, so a
    tenant who signed from here was dropped onto the old dashboard -- which
    is precisely what this surface's copy told them would not happen. The
    hook is written to be shared, but V1 does NOT use it yet: V1's own
    effect also drives its tab selection and a persistent expired-session
    notice with no V2 equivalent, so collapsing it means rewriting V1's
    signing path. Two implementations of one contract-critical hop is a
    drift risk; it is flagged on the PR, not pretended away here.
  */
  const roomLabels = getTenantRoomLabels(baseLanguageFor(activeLanguage));
  useDocusignSigningReturn({
    bookingId: booking?.id != null ? Number(booking.id) : null,
    labels: useMemo(
      () => ({
        signed: roomLabels.signedSuccessfully,
        syncFailed: roomLabels.signingSyncFailed,
        sessionExpired: roomLabels.signingSessionExpired,
      }),
      [roomLabels]
    ),
    notify: DOCUSIGN_RETURN_TOASTS,
  });

  /*
    Own the Stripe return hop too (COL-533). Stripe lands the tenant back on
    /dashboard?payment=success|canceled; DashboardRoute latched those to V1, so
    a tenant who paid from here — including the deposit-by-card path — was
    dropped onto the old dashboard. Same injected-toast shape as DocuSign.
  */
  useStripePaymentReturn({
    userId: user?.id ?? null,
    labels: useMemo(
      () => ({
        success: roomLabels.paymentSuccessful,
        canceled: roomLabels.paymentCanceled,
      }),
      [roomLabels]
    ),
    notify: DOCUSIGN_RETURN_TOASTS,
  });

  const money = useTenantMoneyFacts(booking);
  const { data: protocols = [] } = useProtocolsForBooking(booking?.id ?? null);

  const derivedStage = useMemo<TenantJourneyStageId | null>(() => {
    if (!booking) return null;
    // The SHARED signal builder, not a hand-rolled signals object. Building it
    // by hand set only three of the five signals the resolver reads, leaving
    // `depositRefundSettled` and `noDepositHeld` unset — so a refunded tenant
    // parked at Deposit refund pending here while the admin side showed Moved
    // out. Fed the same scoped rows the money figures came from, so the
    // deposit gate and the deposit card cannot disagree.
    //
    // `settlement` is deliberately not passed: V2 does not load
    // deposit_settlements, and an absent source reads as "no evidence", which
    // parks the tenant at the earlier stage rather than promoting them past a
    // gate nothing proved. Both stages show the same tabs today, so this is a
    // correctness gap and not a visible one — worth a row to close it.
    const submittedProtocolKinds = new Set(
      (protocols as any[])
        // `status === "submitted"` is the canonical test (protocol-action.ts),
        // not `submitted_at`, which stays stamped after an admin reopens one.
        .filter((p) => p?.status === "submitted")
        .map((p) => String(p?.kind ?? ""))
    );
    return resolveTenantJourneyFromSources(booking, {
      payments: money.scopedPayments,
      proofFor: money.proofFor,
      submittedProtocolKinds,
    }).stageId;
  }, [booking, money.scopedPayments, money.proofFor, protocols]);

  const stage = stageOverride !== undefined ? stageOverride : derivedStage;

  /*
    A contract signed on paper leaves no artefact, so the stage ladder parks
    that tenant at `applied` -- correctly, it cannot prove the contract. The
    TABS must not follow it there. `isTenancyStarted` covers the whole
    tenancy lifecycle -- including notice_given / move_out_pending /
    moved_out, who need the move-out protocol most and whom the applicant
    frame does not show it to.
  */
  const tenancyStarted = isTenancyStarted((booking as any)?.status);

  const visible = useMemo(
    () => visibleTabsForStage(stage, hasBooking, tenancyStarted),
    [stage, hasBooking, tenancyStarted]
  );

  // Which frame this is. `hasBooking` cannot answer it any more: an applicant
  // waiting on a decision has a `pending` booking row, and the applicant tabs
  // are what the stage resolves to.
  const isApplicantFrame = visible.includes("applications");

  // A `?tab=` value may predate V2 — five edge functions still email the old
  // names — so it always goes through the mapper, never a direct comparison.
  const activeTab = resolveActiveTab(
    resolveLegacyTabParam(searchParams.get("tab")),
    stage,
    hasBooking,
    tenancyStarted
  );

  const selectTab = (tab: DashboardV2Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    // Opening a tab by hand cancels a search scroll still waiting to happen.
    next.delete(SEARCH_TARGET_PARAM);
    setSearchParams(next, { replace: true });
  };

  /*
    A search result opens its tab AND the block it is about (COL-623). The
    block rides in `?section=` beside `?tab=`, so it is a plain link too;
    the hook scrolls to it once it has rendered, then drops the parameter
    so a refresh or Back does not scroll again. Picking the same result
    twice sets it again, so it scrolls again — from My room itself, which
    before only re-set `?tab=room` and visibly did nothing.
  */
  const openSearchResult = (tab: DashboardV2Tab, target?: SearchTarget) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (target) next.set(SEARCH_TARGET_PARAM, target);
    else next.delete(SEARCH_TARGET_PARAM);
    setSearchParams(next, { replace: true });
  };

  const wantedTarget = searchParams.get(SEARCH_TARGET_PARAM);
  const dropSearchTarget = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SEARCH_TARGET_PARAM);
        return next;
      },
      { replace: true }
    );
  useScrollToSearchTarget(
    isSearchTarget(wantedTarget) ? wantedTarget : null,
    dropSearchTarget
  );
  // A `?section=` nothing renders (a stale or hand-edited link) is dropped
  // too, so the parameter is one-shot whatever its value. Keyed on the whole
  // `searchParams`: the `?settings=` cleanup below can write a stale copy
  // back in the same commit, and this has to run again to drop it then.
  const unknownTarget = wantedTarget !== null && !isSearchTarget(wantedTarget);
  useEffect(() => {
    if (unknownTarget) dropSearchTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /*
    Settings opens HERE now, not by falling back to V1.

    The nav deep-links `/dashboard?settings=<tab>`, and `settings` used to be
    one of DashboardRoute's V1-only latch params -- so a tenant on V2 who
    opened Settings was thrown back to the nine-tab V1 dashboard, with the
    modal floating over it. The dialog was already extracted to
    components/settings/AccountSettingsDialog precisely so other surfaces
    could mount it; V2 just never did.

    Same alias table and same param-stripping as V1's copy, so a link written
    against the displayed labels ("personal", "login") lands in the same
    place on either dashboard, and a refresh or Back does not reopen it.
  */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<AccountSettingsTab>("profile");

  useEffect(() => {
    const wanted = searchParams.get("settings");
    if (!wanted) return;
    const aliases: Record<string, AccountSettingsTab> = {
      profile: "profile",
      personal: "profile",
      documents: "documents",
      language: "language",
      password: "password",
      login: "password",
    };
    setSettingsTab(aliases[wanted] ?? "profile");
    setSettingsOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("settings");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  /*
    The same dialog V1 mounts, so Settings reads identically on both.

    Held in a variable because it has to render on the loading and error
    branches below as well as the main one. The effect above consumes and
    strips `?settings=` whatever the booking query is doing, so mounting the
    dialog only in the happy path meant a tenant whose booking failed to load
    clicked Settings, watched the URL clean itself, and got nothing -- losing
    profile and password, neither of which needs a booking.

    `documentsSlot` is what decides whether the Documents TAB renders at
    all -- without it the tab silently disappears, which would trade one
    regression for another. V2 does not own document upload (that stayed
    in V1 deliberately, and TenantApplications already hands off to it),
    so the slot is a short pointer to the same place rather than a second
    uploader writing the same tenant_documents rows.
  */
  const settingsDialog = (
    <AccountSettingsDialog
      open={settingsOpen}
      onOpenChange={setSettingsOpen}
      initialTab={settingsTab}
      /*
        The phone the tenant already gave us. `profiles.phone` is often
        empty while the booking carries the number from their application,
        and the dialog falls back to it for both the displayed value and
        the edit form. V1 passes this; without it, Settings on V2 shows
        "Not set" to a tenant whose number we hold. (Codex, 16 Oct.)
      */
      phoneFallback={
        (booking as { phone?: string | null } | null)?.phone?.trim() ?? ""
      }
      documentsSlot={
        /*
          TabsContent, not a bare div. The dialog drops `documentsSlot`
          straight into its Tabs container without wrapping it, so an
          unwrapped node renders on EVERY tab -- Profile, Language and
          Password included. V1 wraps its slot the same way.
        */
        <TabsContent value="documents" className="m-0 space-y-3">
          <p className="text-sm text-muted-foreground">
            {d.settingsDocumentsHandoff}
          </p>
          <Link
            to="/dashboard?dashboard=v1&settings=documents"
            className="inline-block text-sm font-semibold underline underline-offset-4"
          >
            {d.settingsDocumentsOpen}
          </Link>
        </TabsContent>
      }
    />
  );

  const tabLabel = (tab: DashboardV2Tab): string =>
    ({
      overview: d.tabOverview,
      room: d.tabRoom,
      community: d.tabCommunity,
      referrals: d.tabReferrals,
      help: d.tabHelp,
      applications: d.tabApplications,
      saved: d.tabSaved,
    })[tab];

  // profiles.name is the system of record. auth user_metadata is whatever was
  // typed at signup and is never corrected when an admin fixes a tenant's
  // name, so greeting from it can use a name no other screen shows.
  const { data: profileName } = useTenantProfileName();
  const firstName = String(profileName ?? "")
    .trim()
    .split(/\s+/)[0];
  // Ticks every minute, so the greeting crosses 11:00 / 18:00 on a dashboard
  // left open rather than staying on whatever it mounted with.
  const now = useNowTick();
  /*
    COL-368's action-needed pip, which Neel named as a requirement for this
    ticket on 5 Oct: "important to this ticket is making sure to keep COL-368
    with the hotspots/badges for open todos for tenants."

    V1's tab strip renders it (Dashboard.tsx); V2's did not, so a tenant with
    a handover due got no indicator on Mein Zimmer at all. Same hook, so the
    pip and the card inside the tab cannot disagree.
  */
  const protocolAction = useProtocolActionNeeded();

  /*
    The keys being out is an open todo too (Neel, 23 Oct).

    The strip asked `useProtocolActionNeeded()` alone, and that hook goes
    quiet the moment the move-out protocol is submitted -- which is exactly
    when `key_return_pending` begins. So the Ubersicht hero said "Return your
    keys" while Mein Zimmer, the tab holding the key-return card, carried no
    pip at all: the disagreement this strip was written to prevent.

    Read off the SAME derived stage the hero's fact comes from, so the two
    cannot drift. Note this strip does not consume `computeTenantNextActions`
    the way V1's does -- adding the fact there alone would have left this
    surface, the one tenants actually see, unchanged.
  */
  const keyReturnPending = stage === "key_return_pending";
  const roomNeedsAction = !!protocolAction || keyReturnPending;

  /*
    Three greetings, not two.

    This was `hour < 11 ? morning : "Hi {name}."`, so the dashboard said "Hi"
    from 11:00 until midnight — the whole afternoon and evening — while every
    other surface follows the German convention the copy is written in (Guten
    Morgen / Guten Tag / Guten Abend). The design export only ships the morning
    frame; that is the first case of a three-way split, not the whole rule.

    `now` comes from the same tick the money facts use, so the headline turns
    over at the boundary on a dashboard left open overnight instead of staying
    stuck on the greeting it mounted with.
  */
  const greeting = formatTenantGreeting(
    {
      morning: d.greetingMorning,
      afternoon: d.greetingAfternoon,
      evening: d.greetingEvening,
    }[tenantGreetingSlot(new Date(now))],
    firstName
  );

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-screen-2xl px-6 pb-10 pt-24 lg:px-10">
        <p className="text-muted-foreground">{d.loading}</p>
        {settingsDialog}
      </div>
    );
  }

  // A failed fetch must not look like "you have no booking" — that reads as
  // data loss to a tenant who does have one.
  if (isError) {
    return (
      <div className="mx-auto w-full max-w-screen-2xl px-6 pb-10 pt-24 lg:px-10">
        <Card className="p-6">
          <p className="text-destructive">{d.loadFailed}</p>
        </Card>
        {settingsDialog}
      </div>
    );
  }

  /*
    A protocol deep link outlives the tenancy that produced it.

    `/protocol/:id` redirects to `/dashboard?tab=checkin&p=<id>&step=<x>`
    (App.tsx), and protocol-reminder-tick emails the same shape. Once the
    booking is `moved_out` or `terminated` `useTenantBooking` returns null, V2
    shows the applicant tabs, and `resolveActiveTab` clamps `room` away — so
    the protocol the link names can never mount. V1's nine tabs are always
    present, so it can still open it. Hand those links over rather than
    swallowing them, carrying `p` and `step` across intact.
  */
  if (!hasBooking && searchParams.has("p")) {
    const handoff = new URLSearchParams();
    handoff.set(FORCE_V1_PARAM, FORCE_V1_VALUE);
    handoff.set("tab", "checkin");
    handoff.set("p", searchParams.get("p") ?? "");
    const step = searchParams.get("step");
    if (step) handoff.set("step", step);
    return <Navigate to={`/dashboard?${handoff.toString()}`} replace />;
  }

  /*
    `pt-24` clears the fixed header. App.tsx mounts <Navigation /> for every
    route, including /dashboard; it is `fixed top-0 z-50` and ~72px tall, so
    without this the tab strip renders underneath it and cannot be clicked.
    V1 carries the same pt-24 on each of its top-level containers. Component
    tests render this page alone and never mount the header, so no test here
    can catch it.
  */
  return (
    /*
      Two layers: the outer owns the page background and is full width, the
      inner centres and pads the content. The footer sits BETWEEN them, so it
      spans the viewport without breaking out of a padded container.
    */
    <div className="tenant-dashboard-v2 w-full bg-background pt-24 text-foreground">
      <div className="mx-auto w-full max-w-screen-2xl px-6 pb-8 lg:px-10">
        {/*
        First-login language picker (COL-478). V1 mounts this top-level; with
        the flag on, V2 renders instead and an eligible tenant
        (`profiles.language_chosen_at` still null) would never be asked — which
        quietly removes the onboarding step the six-language release depends on.
        The component self-gates on role, flag and that column, and hides on any
        error, so mounting it here is the whole fix.
      */}
        <TenantLanguagePicker />
        {/*
        The design puts the search box at the right end of the tab strip, so
        the strip and the box share one row. The nav keeps its own overflow
        scrolling for narrow screens; the box drops under it there rather
        than squeezing the tabs.
      */}
        <div className="mb-8 flex flex-col gap-4 border-b border-border/60 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          <nav
            className="flex gap-5 overflow-x-auto sm:gap-6 [&::-webkit-scrollbar]:hidden"
            aria-label={d.tabOverview}
          >
            {visible.map((tab) => {
              const on = tab === activeTab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => selectTab(tab)}
                  aria-current={on ? "page" : undefined}
                  aria-label={
                    tab === "room" && roomNeedsAction
                      ? `${tabLabel(tab)} — ${d.actionNeeded}`
                      : undefined
                  }
                  className={[
                    "-mb-px whitespace-nowrap border-b-2 pb-3 text-base transition-colors sm:text-[17px]",
                    on
                      ? "border-foreground font-semibold text-foreground"
                      : "border-transparent font-medium text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {tabLabel(tab)}
                  {tab === "room" && roomNeedsAction ? (
                    <span
                      aria-hidden
                      className="ml-1.5 inline-block h-[7px] w-[7px] rounded-full bg-[#C0714F] align-middle"
                    />
                  ) : null}
                </button>
              );
            })}
          </nav>
          <div className="pb-3">
            <TenantDashboardSearch
              applicant={isApplicantFrame}
              availableTabs={visible}
              onOpen={openSearchResult}
            />
          </div>
        </div>

        {/*
        The greeting belongs to the SCREEN, not to the shell.

        The design prints it in the Übersicht hero (under a date line, beside
        the room photo) and at the top of Bewerbungen — and nowhere else. A
        shell-level h1 put it above Mein Zimmer, Favoriten and Hilfe too,
        where the design has none.
      */}
        {activeTab === "applications" ? (
          <h1 className="mb-6 text-4xl font-semibold leading-[1.03] tracking-[-0.045em] text-foreground sm:text-5xl">
            {greeting}
          </h1>
        ) : null}

        {activeTab === "applications" ? <TenantApplications /> : null}

        {activeTab === "saved" ? <TenantSavedRooms /> : null}

        {activeTab === "overview" && hasBooking ? (
          <TenantOverview
            booking={booking}
            onOpen={selectTab}
            availableTabs={visible}
            stage={stage}
            greeting={greeting}
          />
        ) : null}

        {activeTab === "community" ? (
          <ComingSoon description={d.tabCommunity} />
        ) : null}

        {activeTab === "referrals" ? <TenantReferrals /> : null}

        {activeTab === "help" ? (
          <TenantSupportHelp applicant={isApplicantFrame} />
        ) : null}

        {activeTab === "room" && hasBooking ? (
          <TenantRoomPage
            booking={booking}
            protocolStepOpen={!!searchParams.get("p")}
          />
        ) : null}
      </div>

      <TenantDashboardFooter
        // The FRAME, not the row: an applicant has a `pending` booking too.
        variant={isApplicantFrame ? "applicant" : "tenant"}
        onReportIssue={() => selectTab("help")}
      />

      {settingsDialog}
    </div>
  );
}
